// API service for diagnosis management
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class DiagnosisService {
  // Fetch every diagnosis of the organization, following pagination
  // (the backend returns at most `limit` items per request, 50 by default).
  static async getAllDiagnoses(token) {
    try {
      const allDiagnoses = [];
      let lastKey = null;
      do {
        const { diagnoses, pagination } = await this.getDiagnoses(token, 200, lastKey);
        allDiagnoses.push(...(diagnoses || []));
        lastKey = pagination?.lastKey || null;
      } while (lastKey);

      return { success: true, data: allDiagnoses };
    } catch (error) {
      console.error('Error fetching all diagnoses:', error);
      throw error;
    }
  }

  static async getDiagnoses(token, limit = 50, lastKey = null) {
    try {
      let url = `${API_BASE_URL}/diagnoses?limit=${limit}`;
      if (lastKey) {
        url += `&lastKey=${encodeURIComponent(JSON.stringify(lastKey))}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        diagnoses: data.data,
        pagination: data.pagination
      };
    } catch (error) {
      console.error('Error fetching diagnoses:', error);
      throw error;
    }
  }

  // Load the organization's full diagnosis catalog as a Map keyed by id.
  // Use this instead of calling getDiagnosisById once per sub-note (N+1).
  static async getDiagnosesMap(token) {
    const { data } = await this.getAllDiagnoses(token);
    return new Map(data.map(diagnosis => [diagnosis.id, diagnosis]));
  }

  static async getDiagnosisById(diagnosisId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/diagnoses/${diagnosisId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching diagnosis:', error);
      throw error;
    }
  }

  static async createDiagnosis(diagnosisData, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/diagnoses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(diagnosisData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error creating diagnosis:', error);
      throw error;
    }
  }

  static async updateDiagnosis(diagnosisId, diagnosisData, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/diagnoses/${diagnosisId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(diagnosisData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error updating diagnosis:', error);
      throw error;
    }
  }

  static async deleteDiagnosis(diagnosisId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/diagnoses/${diagnosisId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting diagnosis:', error);
      throw error;
    }
  }

  static async searchDiagnoses(query, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/diagnoses/search?query=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error searching diagnoses:', error);
      throw error;
    }
  }

  static async getDiagnosisStats(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/diagnoses/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching diagnosis stats:', error);
      throw error;
    }
  }
}

export default DiagnosisService;

